import {
  ActionCreator,
  ActionType,
  createAction as ngrxCreateAction,
  createReducer as ngrxCreateReducer,
  createSelectorFactory,
  MemoizedSelector,
  MemoizedSelectorWithProps,
  on as ngrxOn,
  props as ngrxProps,
  SelectorWithProps,
} from '@ngrx/store';

import { deepEqual } from '@recruitee/utils';

import { memoize } from './selector-utils';
import {
  ActionScopedPayload,
  ActionPayloadFromConfigFn,
  ActionsFromConfig,
  ConfigFn,
  ConfigObject,
  Reference,
  PickUndefined,
  RemoveUndefined,
} from './types';

type RemoveVoid<T> = T extends void ? never : T;

export const ngrxEvent = <T>(): RemoveVoid<T> extends void
  ? <S>(state: S) => S
  : <S>(state: S, payload: T) => S => <any>((state: any) => state);

const createActions = <State, T extends ConfigObject<any>>(
  namespace: string,
  actions: T,
): ActionsFromConfig<T> => {
  const entries = <[Extract<keyof T, string>, ConfigFn<State>][]>Object.entries(actions);
  return entries.reduce((acc, [actionName, handler]) => {
    const actionCreator = ngrxCreateAction(
      `[${namespace}] ${actionName}`,
      ngrxProps<{ payload: ActionScopedPayload<ActionPayloadFromConfigFn<typeof handler>> }>(),
    );

    // @ts-ignore
    acc[actionName] = Object.assign(actionCreator, { namespace, actionName });

    return acc;
  }, <ActionsFromConfig<T>>{});
};

const createReducer = <S, C extends ConfigObject<S>>(
  state: S,
  config: C,
  actions: ActionsFromConfig<C>,
) => {
  const actionEntries = Object.entries(actions);
  return ngrxCreateReducer(
    state,
    ...actionEntries.map(([key, action]) =>
      ngrxOn(
        action,
        (
          currentState: S,
          actionInstance: ActionType<
            ActionCreator<
              string,
              (options: { payload?: ActionScopedPayload<any> }) => {
                payload?: ActionScopedPayload<any>;
              }
            >
          >,
        ) => config[key](currentState, actionInstance.payload),
      ),
    ),
  );
};

// prettier-ignore
type SelectorWithRef<State extends object, Result, Props, P2 = Props> = PickUndefined<Props> extends never
  ? MemoizedSelectorWithProps<State, P2, Result>
  : RemoveUndefined<Props> extends never
    ? MemoizedSelector<State, Result>
    : MemoizedSelectorWithProps<State, P2, Result> & MemoizedSelector<State, Result>;

export const arrayIsDifferent = (arr1: any[], arr2: any[]): boolean => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return true;
  if (arr1.length !== arr2.length) return true;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i] && !Array.isArray(arr1[i])) {
      return true;
    }
    if (Array.isArray(arr1[i]) && arrayIsDifferent(arr1[i], arr2[i])) return true;
  }
  return false;
};
type Cache = { result: any; main: any[]; references: any[] };
// eslint-disable-next-line max-lines-per-function
export const createSelectorWithReferences = <State extends object, Result = any, Props = void>(
  baseStateSelector: MemoizedSelectorWithProps<State, any, Result>,
  referenceSelectors: Reference<any>[] | null,
  resultFn: (state: Result, props: Props) => Result,
): SelectorWithRef<State, Result, Props> => {
  const referencesConfig = referenceSelectors || [];

  let previousProps: any;
  let previousStateChunks: any[] | null = null;
  let resultCache: any = null;

  const objectCache = new WeakMap<object, Cache>();
  const primitiveCache = new Map<string | number | null, Cache>();

  const selectorFactory = createSelectorFactory(
    memoize({ ignoreProps: false, projectorFn: resultFn }),
    {
      // eslint-disable-next-line max-lines-per-function
      stateFn: (rootState, selectors, props, memoizedProjector) => {
        const [mainSelector] = <SelectorWithProps<any, any, any>[]>selectors;
        const mainState = mainSelector(rootState, props);
        const isArray = Array.isArray(mainState);
        const mainStateArr = isArray ? mainState : [mainState];
        const cache = <Map<any, Cache>>(
          (typeof props === 'object' && props !== null ? objectCache : primitiveCache)
        );

        const cacheStateChunks: any[] = [];
        const referencesArr: any[] = [];
        const result = mainStateArr.map((entity: any) => {
          cacheStateChunks.push(entity);
          const copy = { ...entity };
          for (let i = 0; i < referencesConfig.length; i++) {
            const { mapFrom, mapTo, source } = referencesConfig[i];
            const ref = source(rootState, entity[mapFrom]);
            copy[mapTo] = ref;
            referencesArr.push(ref);
            cacheStateChunks.push(ref);
          }
          return copy;
        });

        if (cache.has(props)) {
          const cachedData = cache.get(props)!;
          if (
            !arrayIsDifferent(cachedData.main, mainStateArr) &&
            !arrayIsDifferent(cachedData.references, referencesArr)
          ) {
            return memoizedProjector.memoized(cachedData.result, props);
          }
        }

        cache.set(props, {
          main: mainStateArr,
          references: referencesArr,
          result: isArray ? result : result[0],
        });

        const isPropsChanged =
          typeof props === 'object' && props !== null
            ? !deepEqual(previousProps, props)
            : previousProps !== props;

        const cachedArgsChanged =
          isPropsChanged ||
          (previousStateChunks && cacheStateChunks.length === previousStateChunks.length
            ? previousStateChunks.some((arg, i) =>
                Array.isArray(cacheStateChunks[i])
                  ? arrayIsDifferent(cacheStateChunks[i], arg)
                  : cacheStateChunks[i] !== arg,
              )
            : true);

        if (cachedArgsChanged) {
          previousProps = props;
          previousStateChunks = cacheStateChunks;
          resultCache = isArray ? result : result[0];
        }

        return memoizedProjector.memoized(resultCache, previousProps);
      },
    },
  );

  return <any>(
    selectorFactory([baseStateSelector, ...referencesConfig.map(({ source }) => source), resultFn])
  );
};

export const createActionsWithReducer = <NS extends string, State, T extends ConfigObject<State>>(
  namespace: NS,
  state: State,
  actionReducerMap: T,
) => {
  const actions = createActions<State, T>(namespace, actionReducerMap);

  return { actions, reducer: createReducer<State, T>(state, actionReducerMap, actions) };
};

export const pipeState = <T>(state: T, ...transformFns: ((s: T) => T)[]): T =>
  transformFns.reduce((previousState, transformFn) => transformFn(previousState), state);
